import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  useMediaQuery,
  useTheme
} from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";
import CloseIcon from "@material-ui/icons/Close";
import "./Chat.css";

type Props = {
  messages: any;
  zoomClient: any;
};

export const ChatWindow: React.FC<Props> = ({
  messages,
  zoomClient
}: Props) => {
  const [messageText, setMessageText] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Scroll to bottom whenever messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  };

  const handleSendMessage = () => {
    if (messageText.trim() && zoomClient) {
      const chatClient = zoomClient.getChatClient();
      chatClient.sendToAll(messageText.trim());
      setMessageText("");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getFormattedTime = (timestamp: number) => {
    return new Date(timestamp)
      .toLocaleTimeString("en-us", { hour: "numeric", minute: "numeric" })
      .toLowerCase();
  };

  // Check if the current user is the sender of the message
  const isLocalUser = (senderId: string) => {
    return zoomClient && senderId === zoomClient.getCurrentUserInfo()?.userId;
  };

  return (
    <div className="zoom-chat-container">
      <div className="zoom-chat-header">
        <Typography variant="subtitle1">Chat</Typography>
        {isMobile && (
          <IconButton size="small" onClick={() => window.history.back()}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </div>

      <div className="zoom-chat-messages" ref={messagesContainerRef}>
        {messages.length === 0 ? (
          <Box p={2} textAlign="center" color="text.secondary">
            <Typography variant="body2">No messages yet</Typography>
          </Box>
        ) : (
          messages.map((msg: any, index: number) => {
            const isLocal = isLocalUser(msg.sender?.userId);
            return (
              <div
                key={msg.id || index}
                className={`zoom-message ${isLocal ? "local" : ""}`}
              >
                <div className="zoom-message-info">
                  <span>
                    {msg.sender?.name || "Unknown"}
                    {isLocal ? " (You)" : ""}
                  </span>
                  <span>{getFormattedTime(msg.timestamp)}</span>
                </div>
                <div
                  className={`zoom-message-bubble ${isLocal ? "local" : ""}`}
                >
                  {msg.message}
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="zoom-chat-input">
        <div className="zoom-chat-input-container">
          <textarea
            className="zoom-chat-textarea"
            placeholder="Type a message..."
            value={messageText}
            onChange={e => setMessageText(e.target.value)}
            onKeyPress={handleKeyPress}
            rows={1}
          />
          <button
            className="zoom-chat-send-button"
            onClick={handleSendMessage}
            disabled={!messageText.trim()}
          >
            <SendIcon fontSize="small" />
          </button>
        </div>
      </div>
    </div>
  );
};
